import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, Copy, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const CategoryImportDialog = ({ departmentId, departments = [], onClose, onApplied }) => {
  const [sourceDepartmentId, setSourceDepartmentId] = useState('');
  const [preview, setPreview] = useState(null);
  const [replaceAssignments, setReplaceAssignments] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const payload = () => ({
    source_department_id: sourceDepartmentId,
    replace_existing_assignments: replaceAssignments,
  });

  const loadPreview = async () => {
    if (!sourceDepartmentId) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${API}/department-admin/${departmentId}/category-import/preview`,
        payload()
      );
      setPreview(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Die Vorschau konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${API}/department-admin/${departmentId}/category-import/apply`,
        payload()
      );
      onApplied(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Die Kategorien konnten nicht übernommen werden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:items-center" role="dialog" aria-modal="true" aria-label="Kategorien übernehmen">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <header className="flex items-start justify-between border-b p-5">
          <div className="flex gap-3"><span className="rounded-xl bg-blue-100 p-2 text-blue-700"><Copy /></span><div><h2 className="text-xl font-bold">Kategorien übernehmen</h2><p className="text-sm text-gray-500">Aus einer anderen Wachabteilung kopieren</p></div></div>
          <button type="button" onClick={onClose} aria-label="Dialog schließen" className="min-h-11 min-w-11 rounded-lg p-2 text-gray-500 hover:bg-gray-100"><X /></button>
        </header>

        <div className="space-y-5 p-5">
          {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          <label className="block text-sm font-semibold text-gray-700">Quell-Wachabteilung
            <select value={sourceDepartmentId} onChange={event => { setSourceDepartmentId(event.target.value); setPreview(null); }} className="mt-1 min-h-11 w-full rounded-xl border border-gray-300 px-3">
              <option value="">Bitte auswählen</option>
              {departments.filter(department => department.id !== departmentId).map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
            </select>
          </label>
          <button type="button" data-testid="category-import-preview" disabled={!sourceDepartmentId || loading} onClick={loadPreview} className="min-h-11 w-full rounded-xl bg-blue-600 px-4 font-semibold text-white disabled:bg-gray-300">Vorschau anzeigen</button>

          {preview && <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div><h3 className="font-semibold text-gray-800">Werden neu angelegt</h3>{preview.categories_to_create.length ? <ul className="mt-2 space-y-1 text-sm">{preview.categories_to_create.map(item => <li key={item.id}>+ {item.name}</li>)}</ul> : <p className="mt-2 text-sm text-gray-500">Keine</p>}</div>
              <div><h3 className="font-semibold text-gray-800">Bleiben erhalten</h3>{preview.reused_categories.length ? <ul className="mt-2 space-y-1 text-sm">{preview.reused_categories.map(item => <li key={item.id}>{item.name}</li>)}</ul> : <p className="mt-2 text-sm text-gray-500">Keine</p>}</div>
            </div>
            <div><h3 className="font-semibold text-gray-800">Mitarbeiterzuordnungen</h3><p className="mt-1 text-sm text-gray-600">{preview.assignments.length} Zuordnungen können übernommen werden.</p></div>
            {preview.conflicts.length > 0 && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"><h3 className="font-semibold text-amber-900">Bestehende Zuordnungen</h3><p className="text-sm text-amber-800">{preview.conflicts.map(item => item.employee_name).join(', ')}</p><label className="mt-3 flex items-start gap-3 text-sm font-medium text-amber-950"><input name="replace-assignments" type="checkbox" checked={replaceAssignments} onChange={event => setReplaceAssignments(event.target.checked)} className="mt-1 h-4 w-4" /> Bestehende Zuordnungen ersetzen</label></div>}
            <button type="button" data-testid="category-import-apply" disabled={loading} onClick={apply} className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 font-semibold text-white hover:bg-emerald-700"><ArrowRight className="h-4 w-4" /> Kategorien übernehmen</button>
          </div>}
        </div>
      </div>
    </div>
  );
};

export default CategoryImportDialog;
